import { Suspense, lazy, useCallback, useRef } from "react";
import { addLocale } from "primereact/api";
import { Outlet, useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import { Toaster } from "sonner";
import "./App.css";
import AuthContextProvider from "./common/AuthContext";
import { isKakaoTalkWebView } from "./common/Util";
import KakaoBlockPage from "./components/KakaoBlockPage";
import "./globals.css";
import SeoHead from "./seo/SeoHead";
import { PathProvider } from "./v1/context/PathTracker";
import { kakaoModalState } from "./atoms/KakaoModalAtom";
import { ScrollToTop } from "./components/ScrollToTop";
import Navbar from "./v1/ui/navbar/Navbar";
import { TopBanner } from "./v1/ui/banner/TopBanner";
import { useTossPixelPageView } from "./hooks/useAds";
import FullScreenSpinner from "./components/FullScreenSpinner";
import { PrerenderBoundaryMark } from "./common/prerender";

// antd-based modal: loaded on first open so antd stays out of the landing bundle
const KakaoChannelGuideModal = lazy(
  () => import("./components/KakaoChannelGuideModal"),
);

addLocale("kr", {
  firstDayOfWeek: 1,
  dayNames: [
    "일요일",
    "월요일",
    "화요일",
    "수요일",
    "목요일",
    "금요일",
    "토요일",
  ],
  dayNamesShort: ["일", "월", "화", "수", "목", "금", "토"],
  dayNamesMin: ["일", "월", "화", "수", "목", "금", "토"],
  monthNames: [
    "1월",
    "2월",
    "3월",
    "4월",
    "5월",
    "6월",
    "7월",
    "8월",
    "9월",
    "10월",
    "11월",
    "12월",
  ],
  monthNamesShort: [
    "1월",
    "2월",
    "3월",
    "4월",
    "5월",
    "6월",
    "7월",
    "8월",
    "9월",
    "10월",
    "11월",
    "12월",
  ],
  today: "오늘",
  clear: "초기화",
});

const HIDE_NAVBAR_PATHS = [
  "/chatlist",
  "/signin",
  "/cq",
  "/infoTelling",
  "/pbs",
  "/enforcement2", // 강제집행 v2: 목업처럼 전체 화면 (푸터도 없음 — index.js 라우트 참고)
  // 채무자 셀프 분납조정: 글로벌 마케팅 헤더 대신 슬림 신뢰 헤더(로고+대표변호사)만 노출
  "/self-adjust",
];

const V1_PATHS = [
  "/",
  "/get-predata",
  "/predata-login",
  "/marketing-landing",
  "/analyze",
  "/full-sue",
];

const isDesktopHost = () =>
  window.location.host.startsWith("sm.") ||
  window.location.href.includes("/desk/") ||
  window.location.href.includes("/endorsement");

const getAppClassName = (pathname) => {
  if (isDesktopHost()) return "Desktop";

  if (
    V1_PATHS.includes(pathname) ||
    pathname.startsWith("/company") ||
    pathname.includes("/chatlist")
  ) {
    return "App-v1";
  }

  return "App";
};

function App() {
  const [showKakaoModal, setShowKakaoModal] = useRecoilState(kakaoModalState);
  // Mount the modal only after it is first opened, and keep it mounted for the close animation
  const kakaoModalOpenedRef = useRef(false);
  if (showKakaoModal) kakaoModalOpenedRef.current = true;
  const location = useLocation();
  useTossPixelPageView();

  // 랜딩페이지(/)에서만 띠배너를 헤더 위에 표시
  const isLanding = location.pathname === "/";

  // 상단 헤더(띠배너+네브바)의 실제 높이를 측정해 CSS 변수(--header-h)로 저장한다.
  // 히어로 섹션이 이 값으로 "100dvh - 헤더높이"를 계산하므로, 배너 유무·크기가 바뀌어도 자동으로 맞춰진다.
  //
  // useEffect 대신 callback ref를 사용한다.
  // - 헤더 DOM이 붙는 commit 시점에 동기적으로 실행 → 첫 페인트 전에 값이 정해져 깜빡임이 없다.
  // - 배너를 닫으면 래퍼는 그대로지만 높이가 바뀌므로, ResizeObserver로 변화를 계속 감지한다.
  //   (observerRef는 DOM 핸들이 아니라 정리용 observer 인스턴스를 담아두는 용도)
  const observerRef = useRef(null);
  const setHeaderRef = useCallback((node) => {
    observerRef.current?.disconnect(); // 노드 교체/언마운트 시 이전 observer 정리
    if (!node) {
      // 랜딩을 떠나면 다른 페이지에 stale 값이 남지 않도록 변수 제거
      document.documentElement.style.removeProperty("--header-h");
      return;
    }

    const updateHeaderHeight = () => {
      document.documentElement.style.setProperty(
        "--header-h",
        `${node.offsetHeight}px`,
      );
    };

    updateHeaderHeight();
    observerRef.current = new ResizeObserver(updateHeaderHeight);
    observerRef.current.observe(node);
  }, []);

  // 카카오톡 인앱 브라우저 차단 → 외부 브라우저로 유도
  if (isKakaoTalkWebView()) {
    const currentUrl = window.location.href;
    window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(currentUrl)}`;
    return <KakaoBlockPage />;
  }

  // sm. 도메인으로 접속 시 /b2b로 리다이렉트
  if (window.location.host.startsWith("sm.") && location.pathname === "/") {
    window.location.href = "/b2b";
    return null;
  }

  const showNavbar =
    !window.location.host.startsWith("sm.") &&
    !HIDE_NAVBAR_PATHS.some((p) => location.pathname.includes(p));

  return (
    <PathProvider>
      <AuthContextProvider>
        <ScrollToTop />
        <SeoHead />
        <div className={getAppClassName(location.pathname)}>
          {showNavbar &&
            (isLanding ? (
              // 띠배너 + 헤더를 한 묶음으로 상단 고정 (sticky 래퍼가 고정을 담당)
              <div ref={setHeaderRef} className="sticky top-0 z-50">
                <TopBanner />
                <Navbar sticky={false} />
              </div>
            ) : (
              <Navbar />
            ))}
          <Suspense fallback={<FullScreenSpinner />}>
            {/* Boundary marks become <!--$-->…<!--/$--> in the prerender snapshot so hydrateRoot can claim this Suspense */}
            <PrerenderBoundaryMark />
            <Outlet />
            <PrerenderBoundaryMark end />
          </Suspense>
        </div>

        {kakaoModalOpenedRef.current && (
          <Suspense fallback={null}>
            <KakaoChannelGuideModal
              open={showKakaoModal}
              setOpen={setShowKakaoModal}
            />
          </Suspense>
        )}
        <Toaster position="top-center" richColors />
      </AuthContextProvider>
    </PathProvider>
  );
}

export default App;
